import { WordFragment } from "../create";
import { CreateElement } from "./element";
import { CreateParagraph, OptionsParagraph } from "./paragraph";
import { CreateText, OptionsText } from "./text";

interface BorderCell {
    /** Color: HEX value. Default as auto */
    color?: string;
    /** Space: pt value. Default as 0 */
    space?: number;
    /** Border style: single, dotDash, none, thick... Default as single */
    style?: string;
    /** Width: eights of a point value. Default as 4. Minimum 2 and maximum 96 */
    width?: number;
}

interface OptionsCellTable {
    /** Background color: HEX value */
    backgroundColor?: string;
    /** Border */
    border?: {
        bottom?: BorderCell;
        left?: BorderCell;
        right?: BorderCell;
        top?: BorderCell;
    };
    /** Grid span */
    gridSpan?: number;
    /** Fit text */
    fitText?: boolean;
    /** Margin: twips value */
    margin?: {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
    };
    /** Content */
    text: string | WordFragment;
    /** Text direction */
    textDirection?: "btLr" | "tbLrV" | "tbRl" | "tbRlV" | "lrTb" | "lrTbV";
    /** Vertical alignment */
    verticalAlign?: "both" | "bottom" | "center" | "top";
    /** Width: twips value */
    width?: number;
    /** Vertical merge */
    vMerge?: "restart" | "continue";
    /** Wrap text */
    wrapText?: boolean;
}

interface OptionsRowTable {
    /** Allow row to break accross pages */
    cantSplit?: boolean;
    /** Repeat as header row at the top of each page */
    header?: boolean;
    /** Height */
    height?: {
        /** Height size rule */
        rule?: "auto" | "atLeast" | "exact";
        /** Value: twips value */
        value?: number;
    };
}

interface OptionsTable {
    /** Alignment */
    align?: string;
    /** Border */
    border?: {
        bottom?: BorderCell;
        insideH?: BorderCell;
        insideV?: BorderCell;
        left?: BorderCell;
        right?: BorderCell;
        top?: BorderCell;
    };
    /** Cell margins: twips value */
    cellMargin?: {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
    };
    /** Cell spacing: twips value */
    cellSpacing?: number;
    /** Column widths */
    columnWidths?: number | number[];
    /** Contional formatting  */
    conditionalFormatting?: {
        /** First column. Default as true */
        firstColumn?: boolean;
        /** First row.  Default as true */
        firstRow?: boolean;
        /** Last column.  Default as false */
        lastColumn?: boolean;
        /** Last row.  Default as false */
        lastRow?: boolean;
        /** Horizontal banding. Default as false */
        noHBand?: boolean;
        /** Vertical banding.  Default as true */
        noVBand?: boolean;
    };
    /** Indentation: twips value */
    indentation?: number;
    /** Layout */
    layout?: "autofit" | "fixed";
    /** Paragraph options added after the table */
    paragraphAfterOptions?: OptionsParagraph;
    /** Table style. Default as TableGrid */
    tblStyle?: string;
    /** Text wrapping */
    textWrapping?: "around" | "none";
    /** Table width */
    width?: {
        /** Type: auto, dxa (twips value), pct (percent). Default as pct */
        type?: "auto" | "dxa" | "pct";
        /** Value: dxa (twips value), pct (percent value). Default as 100 if type is pct  */
        value?: number;
    };
}

/**
 * Create table
 */
class CreateTable extends CreateElement {
    /**
     * Items
     */
    protected items: (string | WordFragment | OptionsCellTable)[][];

    /**
     * Element options row
     */
    protected optionsRowTable: OptionsRowTable[];

    /**
     * Element options table
     */
    protected optionsTable: OptionsTable;

    /**
     * Contents
     */
    protected contents: string;

    /**
     * Constructor
     *
     * @param optionsTable - Options table
     */
    constructor(
        items: (string | WordFragment | OptionsCellTable)[][],
        optionsRowTable: OptionsRowTable[],
        optionsTable: OptionsTable
    ) {
        super();

        // default options
        optionsTable.tblStyle ??= "TableGrid";
        optionsTable.width ??= { type: "pct", value: 100 };
        optionsTable.paragraphAfterOptions ??= {};

        this.items = items;
        this.optionsRowTable = optionsRowTable;
        this.optionsTable = optionsTable;

        this.contents = "";
    }

    /**
     * Creates content
     *
     * @returns Contents
     */
    public create(): string {
        let contents = "";

        //  table styles
        contents += "<w:tbl><w:tblPr>";
        contents += '<w:tblStyle w:val="' + this.optionsTable.tblStyle + '"/>';
        if (
            this.optionsTable.textWrapping != null &&
            this.optionsTable.textWrapping == "around"
        ) {
            contents +=
                '<w:tblpPr w:leftFromText="141" w:rightFromText="141" w:tblpXSpec="center" w:tblpY="1" w:vertAnchor="text"/>';
        }
        contents += '<w:tblOverlap w:val="never"/>';
        if (this.optionsTable.width != null) {
            if (this.optionsTable.width.type == "auto") {
                this.optionsTable.width.value ??= 0;
            }
            if (this.optionsTable.width.type == "pct") {
                this.optionsTable.width.value ??= 100;
                // pct values are calculated with the following calculation: %value * 50
                this.optionsTable.width.value =
                    this.optionsTable.width.value * 50;
            }
            contents +=
                '<w:tblW w:type="' +
                this.optionsTable.width.type +
                '" w:w="' +
                this.optionsTable.width.value +
                '"/>';
        }
        if (this.optionsTable.align != null) {
            contents += '<w:jc w:val="' + this.optionsTable.align + '"/>';
        }
        if (this.optionsTable.cellSpacing != null) {
            contents +=
                '<w:tblCellSpacing w:type="dxa" w:w="' +
                this.optionsTable.cellSpacing +
                '"/>';
        }
        if (this.optionsTable.indentation != null) {
            contents +=
                '<w:tblInd w:type="dxa" w:w="' +
                this.optionsTable.indentation +
                '"/>';
        }
        if (this.optionsTable.border != null) {
            contents += "<w:tblBorders>";
            contents += this.createBorder(
                "top",
                this.optionsTable.border.top ?? {}
            );
            contents += this.createBorder(
                "left",
                this.optionsTable.border.left ?? {}
            );
            contents += this.createBorder(
                "bottom",
                this.optionsTable.border.bottom ?? {}
            );
            contents += this.createBorder(
                "right",
                this.optionsTable.border.right ?? {}
            );
            contents += this.createBorder(
                "insideH",
                this.optionsTable.border.insideH ?? {}
            );
            contents += this.createBorder(
                "insideV",
                this.optionsTable.border.insideV ?? {}
            );
            contents += "</w:tblBorders>";
        }
        if (this.optionsTable.layout != null) {
            contents +=
                '<w:tblLayout w:type="' + this.optionsTable.layout + '"/>';
        }
        if (this.optionsTable.cellMargin != null) {
            contents += "<w:tblCellMar>";
            if (this.optionsTable.cellMargin.top != null) {
                contents +=
                    '<w:top w:type="dxa" w:w="' +
                    this.optionsTable.cellMargin.top +
                    '"/>';
            }
            if (this.optionsTable.cellMargin.left != null) {
                contents +=
                    '<w:left w:type="dxa" w:w="' +
                    this.optionsTable.cellMargin.left +
                    '"/>';
            }
            if (this.optionsTable.cellMargin.bottom != null) {
                contents +=
                    '<w:bottom w:type="dxa" w:w="' +
                    this.optionsTable.cellMargin.bottom +
                    '"/>';
            }
            if (this.optionsTable.cellMargin.right != null) {
                contents +=
                    '<w:right w:type="dxa" w:w="' +
                    this.optionsTable.cellMargin.right +
                    '"/>';
            }
            contents += "</w:tblCellMar>";
        }
        if (this.optionsTable.conditionalFormatting != null) {
            this.optionsTable.conditionalFormatting = {
                ...{
                    firstColumn: true,
                    firstRow: true,
                    lastColumn: false,
                    lastRow: false,
                    noHBand: false,
                    noVBand: true,
                },
                ...this.optionsTable.conditionalFormatting,
            };
            // calculate val, needed by some DOCX readers
            let resultLookVal = 0;
            if (this.optionsTable.conditionalFormatting.firstRow) {
                resultLookVal |= 0x0020;
            }
            if (this.optionsTable.conditionalFormatting.lastRow) {
                resultLookVal |= 0x0040;
            }
            if (this.optionsTable.conditionalFormatting.firstColumn) {
                resultLookVal |= 0x0080;
            }
            if (this.optionsTable.conditionalFormatting.lastColumn) {
                resultLookVal |= 0x0100;
            }
            if (this.optionsTable.conditionalFormatting.noHBand) {
                resultLookVal |= 0x0200;
            }
            if (this.optionsTable.conditionalFormatting.noVBand) {
                resultLookVal |= 0x0400;
            }
            const tblLookValHex = resultLookVal.toString(16);
            let tblLookVal = tblLookValHex.toString();
            if (tblLookValHex.length == 1) {
                tblLookVal = "000" + tblLookValHex;
            } else if (tblLookValHex.length == 2) {
                tblLookVal = "00" + tblLookValHex;
            } else if (tblLookValHex.length == 3) {
                tblLookVal = "0" + tblLookValHex;
            }

            contents +=
                '<w:tblLook w:firstColumn="' +
                (this.optionsTable.conditionalFormatting.firstColumn
                    ? true
                    : false) +
                '" w:firstRow="' +
                (this.optionsTable.conditionalFormatting.firstRow
                    ? true
                    : false) +
                '" w:lastColumn="' +
                (this.optionsTable.conditionalFormatting.lastColumn
                    ? true
                    : false) +
                '" w:lastRow="' +
                (this.optionsTable.conditionalFormatting.lastRow
                    ? true
                    : false) +
                '" w:noHBand="' +
                (this.optionsTable.conditionalFormatting.noHBand
                    ? true
                    : false) +
                '" w:noVBand="' +
                (this.optionsTable.conditionalFormatting.noVBand
                    ? true
                    : false) +
                '" w:val="' +
                tblLookVal.toUpperCase() +
                '"/>';
        }

        contents += "</w:tblPr>";
        // grid columns
        contents += "<w:tblGrid>";
        // get grid columns length
        const gridColumnsNumber = this.items.reduce(
            (maxLength, row) =>
                row.length > maxLength ? row.length : maxLength,
            1
        );
        for (let iGridCol = 0; iGridCol < gridColumnsNumber; iGridCol++) {
            let widthGridCol = 1;
            // check if the grid column has a explict size to be applied
            if (this.optionsTable.columnWidths) {
                if (typeof this.optionsTable.columnWidths === "number") {
                    widthGridCol = this.optionsTable.columnWidths;
                } else if (Array.isArray(this.optionsTable.columnWidths)) {
                    widthGridCol =
                        this.optionsTable.columnWidths[iGridCol] ?? "1";
                }
            }
            contents += '<w:gridCol w:w="' + widthGridCol + '"/>';
        }
        contents += "</w:tblGrid>";

        // iterate and add items
        this.items.forEach((itemRow, itemRowIndex) => {
            // row styles
            contents += "<w:tr><w:trPr>";

            if (this.optionsRowTable[itemRowIndex]?.cantSplit) {
                contents += "<w:cantSplit/>";
            }

            if (this.optionsRowTable[itemRowIndex]?.height) {
                const heightRow = this.optionsRowTable[itemRowIndex]?.height;
                if (
                    heightRow != null &&
                    heightRow.rule != null &&
                    heightRow.value != null
                ) {
                    contents +=
                        '<w:trHeight w:val="' +
                        heightRow.value +
                        '" w:hRule="' +
                        heightRow.rule +
                        '"/>';
                }
            }

            if (this.optionsRowTable[itemRowIndex]?.header) {
                contents += "<w:tblHeader/>";
            }

            // applied from table styles
            if (this.optionsTable.cellSpacing != null) {
                contents +=
                    '<w:tblCellSpacing w:type="dxa" w:w="' +
                    this.optionsTable.cellSpacing +
                    '"/>';
            }

            contents += "</w:trPr>";
            itemRow.forEach((element, elementIndex) => {
                // cell styles and contents
                contents += "<w:tc><w:tcPr>";

                // cell styles

                // applied from table styles
                let cellWidth;
                if (this.optionsTable.columnWidths) {
                    if (typeof this.optionsTable.columnWidths === "number") {
                        cellWidth = this.optionsTable.columnWidths;
                    } else if (
                        Array.isArray(this.optionsTable.columnWidths) &&
                        this.optionsTable.columnWidths[elementIndex] != null
                    ) {
                        cellWidth =
                            this.optionsTable.columnWidths[elementIndex];
                    }
                }
                let cellBorder;
                let cellColor;
                let cellFitText;
                let cellGridSpan;
                let cellMargin;
                let cellTextDirection;
                let cellVerticalAlign;
                let cellVMerge;
                let cellWrap;

                if (
                    typeof element === "object" &&
                    !(element instanceof WordFragment) &&
                    element.text != null
                ) {
                    // OptionsCellTable type
                    if (element.backgroundColor != null) {
                        cellColor = element.backgroundColor;
                    }
                    if (element.border != null) {
                        cellBorder = "<w:tcBorders>";
                        if (element.border["top"] != null) {
                            cellBorder += this.createBorder(
                                "top",
                                element.border["top"]
                            );
                        }
                        if (element.border["left"] != null) {
                            cellBorder += this.createBorder(
                                "left",
                                element.border["left"]
                            );
                        }
                        if (element.border["bottom"] != null) {
                            cellBorder += this.createBorder(
                                "bottom",
                                element.border["bottom"]
                            );
                        }
                        if (element.border["right"] != null) {
                            cellBorder += this.createBorder(
                                "right",
                                element.border["right"]
                            );
                        }
                        cellBorder += "</w:tcBorders>";
                    }
                    if (element.gridSpan != null) {
                        cellGridSpan = element.gridSpan;
                    }
                    if (element.fitText != null && element.fitText) {
                        cellFitText = "<w:tcFitText/>";
                    }
                    if (element.margin != null) {
                        cellMargin = "<w:tcMar>";
                        if (element.margin.top != null) {
                            cellMargin +=
                                '<w:top w:type="dxa" w:w="' +
                                element.margin.top +
                                '"/>';
                        }
                        if (element.margin.left != null) {
                            cellMargin +=
                                '<w:left w:type="dxa" w:w="' +
                                element.margin.left +
                                '"/>';
                        }
                        if (element.margin.bottom != null) {
                            cellMargin +=
                                '<w:bottom w:type="dxa" w:w="' +
                                element.margin.bottom +
                                '"/>';
                        }
                        if (element.margin.right != null) {
                            cellMargin +=
                                '<w:right w:type="dxa" w:w="' +
                                element.margin.right +
                                '"/>';
                        }
                        cellMargin += "</w:tcMar>";
                    }
                    if (element.textDirection != null) {
                        cellTextDirection =
                            '<w:textDirection w:val="' +
                            element.textDirection +
                            '"/>';
                    }
                    if (element.verticalAlign != null) {
                        cellVerticalAlign =
                            '<w:vAlign w:val="' + element.verticalAlign + '"/>';
                    }
                    if (element.vMerge != null) {
                        cellVMerge = element.vMerge;
                    }
                    if (element.wrapText != null && !element.wrapText) {
                        cellWrap = "<w:noWrap/>";
                    }
                    if (element.width != null) {
                        cellWidth = element.width;
                    }
                }

                if (cellWidth != null) {
                    contents +=
                        '<w:tcW w:type="' +
                        (cellWidth > 0 ? "dxa" : "auto") +
                        '" w:w="' +
                        cellWidth +
                        '"/>';
                }
                if (cellGridSpan != null) {
                    contents += '<w:gridSpan w:val="' + cellGridSpan + '"/>';
                }
                if (cellVMerge != null) {
                    contents +=
                        cellVMerge == "restart"
                            ? '<w:vMerge w:val="restart"/>'
                            : "<w:vMerge/>";
                }
                if (cellBorder != null) {
                    contents += cellBorder;
                }
                if (cellColor != null) {
                    contents +=
                        '<w:shd w:fill="' + cellColor + '" w:val="clear"/>';
                }
                if (cellWrap != null) {
                    contents += cellWrap;
                }
                if (cellMargin != null) {
                    contents += cellMargin;
                }
                if (cellTextDirection != null) {
                    contents += cellTextDirection;
                }
                if (cellFitText != null) {
                    contents += cellFitText;
                }
                if (cellVerticalAlign != null) {
                    contents += cellVerticalAlign;
                }

                contents += "</w:tcPr>";

                // cell contents
                // a content
                let textContent;

                if (
                    typeof element === "string" ||
                    (typeof element === "object" &&
                        !(element instanceof WordFragment) &&
                        typeof element.text === "string")
                ) {
                    const elementContents =
                        typeof element === "string"
                            ? element
                            : (element.text as string);

                    // generate text content
                    const optionsText: OptionsText = {
                        text: elementContents,
                    };
                    const textElement = new CreateText(optionsText);
                    textContent = textElement.create();

                    // wrap the content in a paragraph
                    const paragraphElement = new CreateParagraph({});
                    contents += paragraphElement.create(textContent);
                } else if (
                    element instanceof WordFragment ||
                    (typeof element === "object" &&
                        element.text instanceof WordFragment)
                ) {
                    const elementWordFragment =
                        element instanceof WordFragment
                            ? element
                            : (element.text as WordFragment);
                    contents += elementWordFragment.toString();

                    // keep external contents from the WordFragment to be added into the document when the WordFragment is added
                    this.mergeExternalDependencies(elementWordFragment);
                }

                contents += "</w:tc>";
            });
            contents += "</w:tr>";
        });

        contents += "</w:tbl>";

        // add an empty paragraph after the table. OOXML requires this to do not generate a corrupted document
        const paragraphElement = new CreateParagraph(
            this.optionsTable.paragraphAfterOptions ?? {}
        );
        contents += paragraphElement.create();

        return contents;
    }

    /**
     * Creates border XML
     *
     * @param target - Border target
     * @param options - Options border
     * @returns Border
     */
    protected createBorder(target: string, options: BorderCell): string {
        const borderDefaults: BorderCell = {
            color: "auto",
            space: 0,
            width: 4,
            style: "single",
        };
        options = { ...borderDefaults, ...options };

        // width must be >=2 && <= 96
        options.width =
            options.width && options.width > 2 && options.width <= 96
                ? options.width
                : 2;

        return (
            "<w:" +
            target +
            ' w:color="' +
            options.color +
            '" w:space="' +
            options.space +
            '" w:sz="' +
            options.width +
            '" w:val="' +
            options.style +
            '"/>'
        );
    }
}

export {
    BorderCell,
    CreateTable,
    OptionsCellTable,
    OptionsRowTable,
    OptionsTable,
};
